import {
  MatchMetricDetail,
  MetricDetail,
  MetricDetailCustomConfig,
  MetricDetailed
} from '../models/metric-detail.models'
import {
  METRIC_DETAIL_POSITION_AVERAGE_BY_PLAYER_POSITION,
  METRIC_DETAIL_OMITTED_PROPERTIES,
  METRIC_DETAIL_AGGREGATION_TEXT_BY_TYPE
} from '@features/metric-detail/constants/metric-detail.constants'
import { omit } from 'lodash-es'
import { DisplayMode } from '@core/enums/display.enums'
import { ContextMenuHolder, ContextMenuEntity } from '@core/models/context-menu.models'
import { TeamHolder } from '@pages/team/models/team.models'
import { PlayerHolder } from '@core/models/player.models'
import { Match } from '@core/models/match.models'
import { getContextWithCompetition } from '@features/rankings/utils/ranking.utils'
import { parseTeamMetricDetailSubHeader } from '@features/metric-detail/parsers/team-metric-detail.parser'
import { parseTeamComparisonMetricDetailSubHeader } from '@features/metric-detail/parsers/team-comparison-metric-detail.parser'
import { parsePlayerMetricDetailSubHeader } from '@features/metric-detail/parsers/player-metric-detail.parser'
import { parsePlayerComparisonMetricDetailSubHeader } from '@features/metric-detail/parsers/player-comparison-metric-detail.parser'
import { parseMatchMetricDetailSubHeader } from '@features/metric-detail/parsers/match-metric-detail.parser'

export const getMetricDetailedText = (
  key: string,
  playerPosition?: string,
  averageKey = 'averagePositionValue',
  overrideAggregationText = {}
) =>
  key === averageKey
    ? METRIC_DETAIL_POSITION_AVERAGE_BY_PLAYER_POSITION[playerPosition]
    : { ...METRIC_DETAIL_AGGREGATION_TEXT_BY_TYPE, ...overrideAggregationText }[key]

export const mapMetricDetailed = (
  metric: MetricDetail,
  playerPosition: string,
  displayMode: DisplayMode
): MetricDetailed[] => {
  const { omitted, averageKey, overrideAggregationText } = getMetricDetailCustomConfigByDisplayMode(displayMode)
  const metricAggregations = omit(metric, [...METRIC_DETAIL_OMITTED_PROPERTIES, ...(omitted ?? [])])
  const { comparison, unit } = metric
  return Object.keys(metricAggregations).map(
    (key) =>
      ({
        id: key,
        text: getMetricDetailedText(
          key,
          playerPosition,
          averageKey ?? 'averagePositionValue',
          overrideAggregationText ?? {}
        ),
        leftUnit: unit,
        leftValue: metric[key],
        isDual: !!comparison,
        ...(comparison ? { rightValue: comparison[key], rightUnit: unit } : {})
      }) as MetricDetailed
  )
}

export const mapMatchMetricDetailed = (metric: MatchMetricDetail): MetricDetailed[] => {
  const metricAggregations = omit(metric, METRIC_DETAIL_OMITTED_PROPERTIES)
  return Object.keys(metricAggregations).map(
    (key) =>
      ({
        id: key,
        text: getMetricDetailedText(key),
        leftUnit: metric[key]?.home?.unit,
        leftValue: metric[key]?.home?.value,
        rightValue: metric[key]?.away?.value,
        rightUnit: metric[key]?.away?.unit,
        isDual: true
      }) as MetricDetailed
  )
}

export const resolveMetricDetailSubHeader = (displayMode: DisplayMode, metricContext: ContextMenuEntity) => {
  const context: ContextMenuHolder = getContextWithCompetition(metricContext)
  switch (displayMode) {
    case DisplayMode.Team:
      return parseTeamMetricDetailSubHeader((context as TeamHolder).team)

    case DisplayMode.ComparedTeam:
      return parseTeamComparisonMetricDetailSubHeader((context as TeamHolder).comparison)

    case DisplayMode.Player:
    case DisplayMode.MatchPlayer:
      return parsePlayerMetricDetailSubHeader((context as PlayerHolder).player)

    case DisplayMode.ComparedPlayer:
    case DisplayMode.MatchComparedPlayer:
      return parsePlayerComparisonMetricDetailSubHeader((context as PlayerHolder).comparison)

    case DisplayMode.Match:
      return parseMatchMetricDetailSubHeader(context as Match)
  }
}

export const getMetricDetailCustomConfigByDisplayMode = (displayMode: DisplayMode): MetricDetailCustomConfig => {
  switch (displayMode) {
    case DisplayMode.ComparedPlayer:
    case DisplayMode.MatchComparedPlayer:
    case DisplayMode.MatchPlayer:
      return {
        omitted: ['averagePositionValue'],
        averageKey: 'averageValue'
      }
    case DisplayMode.Player:
      return {
        averageKey: 'skip',
        overrideAggregationText: {
          averagePositionValue: 'MTR_COMMON_AVERAGE_COMPETITION'
        }
      }
    case DisplayMode.ComparedTeam:
      return {
        averageKey: 'skip',
        omitted: ['averageCompetitionValue']
      }
    default:
      return {}
  }
}
